<script setup lang="ts">
import { computed } from 'vue';

import type { CampaignFormErrors, PeriodData } from '../../types';

import { getMinCampaignStartDate, parseLocalDateString } from '../../utils';

const model = defineModel<PeriodData>({ required: true });

defineProps<{
  errors: CampaignFormErrors;
}>();

const minStartDate = getMinCampaignStartDate();

function update<K extends keyof PeriodData>(key: K, value: PeriodData[K]) {
  model.value[key] = value;
}

const duration = computed(() => {
  if (!model.value.startDate || !model.value.endDate) return '';

  const start = parseLocalDateString(model.value.startDate);
  const end = parseLocalDateString(model.value.endDate);
  if (!start || !end) return '';

  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return '';

  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return `${days} dia${days !== 1 ? 's' : ''}`;
});
</script>

<template>
  <div class="fs-section">
    <div class="fs-header">
      <div class="fs-num">3</div>
      <h4 class="fs-title">Período de vigência</h4>
    </div>
    <p class="fs-sub">Janela em que a campanha fica ativa para os parceiros segmentados.</p>

    <div class="row g-3">
      <div class="col-12 col-md-4">
        <label class="fs-label" for="startDate">
          Início <span class="fs-req">*</span>
        </label>
        <input
          id="startDate"
          :value="model.startDate"
          type="date"
          class="form-control"
          :class="{ 'is-invalid': errors.startDate }"
          :min="minStartDate"
          @input="update('startDate', ($event.target as HTMLInputElement).value)"
        />
        <div v-if="errors.startDate" class="invalid-feedback">{{ errors.startDate }}</div>
      </div>

      <div class="col-12 col-md-4">
        <label class="fs-label" for="endDate">
          Fim <span class="fs-req">*</span>
        </label>
        <input
          id="endDate"
          :value="model.endDate"
          type="date"
          class="form-control"
          :class="{ 'is-invalid': errors.endDate }"
          :min="model.startDate"
          @input="update('endDate', ($event.target as HTMLInputElement).value)"
        />
        <div v-if="errors.endDate" class="invalid-feedback">{{ errors.endDate }}</div>
      </div>

      <div class="col-12 col-md-4">
        <label class="fs-label" for="duration">Duração calculada</label>
        <input
          id="duration"
          :value="duration"
          type="text"
          class="form-control"
          :placeholder="model.startDate && model.endDate ? '—' : 'Informe início e término'"
          disabled
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fs-section {
  padding: 24px;
}

.fs-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.fs-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00b569;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
}

.fs-title {
  font-size: 15px;
  font-weight: 700;
  color: #222222;
  margin: 0;
}

.fs-sub {
  font-size: 12.5px;
  color: #6b7280;
  margin-left: 36px;
  margin-bottom: 16px;
  line-height: 1.4;
}

.fs-label {
  font-size: 12px;
  font-weight: 600;
  color: #222222;
  margin-bottom: 4px;
  display: block;
}

.fs-req {
  color: #ef4444;
}

.form-control {
  padding: 9px 12px;
  border-color: #e4e6eb;
  border-radius: 6px;
  font-size: 13px;

  &:focus {
    border-color: #00b569;
    box-shadow: 0 0 0 3px rgba(0, 181, 105, 0.1);
  }

  &:disabled {
    background: #f5f6f8;
    color: #9ca3af;
  }
}
</style>
